import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Switch,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { getTermSheetApi, saveTermSheetApi } from 'api/termSheetApi';
import {
  getQuickCashflowModel,
  listQuickCashflowModels,
} from 'api/quickCashflowModelApi';
import Card from 'components/card/Card';
import { TwDatePicker } from 'components/DatePicker';
import { PercentageNumberInput, TwNumberInput } from 'components/NumberInput';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { buildTermSheetPreviewParagraphs } from '../buildTermSheetClauses';
import {
  isPreviewInputPart,
  PREVIEW_INPUT_PATTERN,
  unwrapPreviewInput,
} from '../previewInput';
import { stripPreviewInputMarkers } from '../previewInput';
import {
  DEFAULT_COHORT_CASH_FLOW_FOOTNOTE,
  DEFAULT_SECURITY_WORDING,
} from '../buildRepaymentSection';
import {
  FUNDING_FEE_TYPE_PCT,
  FUNDING_FEE_TYPE_USD,
} from '../buildFundingFeeSection';
import {
  applyQuickCashflowPrefillToPayload,
  formatQuickCashflowModelOptionLabel,
} from '../buildTermSheetFieldsFromQuickCashflowModel';
import { downloadTermSheetDocx } from '../exportTermSheetDocx';
import { exportTermSheetToDrive } from '../exportTermSheetToDrive';
import { termSheetDriveExportSuccessToast } from '../termSheetDriveExportToast';
import {
  computeTotalCohortInvestment,
  createDefaultTermSheetPayload,
  createEmptyRoasThreshold,
  parseTermSheetPayload,
  serializeTermSheetPayload,
} from '../termSheetPayload';

function PlaceholderText({ children }) {
  return (
    <Text as="span" bg="yellow.200" px={1}>
      {children}
    </Text>
  );
}

function PreviewParagraph({ title, text }) {
  return (
    <Box mb={4}>
      <Text fontWeight="semibold" mb={1}>
        {title}
      </Text>
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {text.split(PREVIEW_INPUT_PATTERN).map((part, index) =>
          isPreviewInputPart(part) ? (
            <PlaceholderText key={index}>{unwrapPreviewInput(part)}</PlaceholderText>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </Text>
    </Box>
  );
}

export default function TermSheetEditor() {
  const { customerId, termSheetId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { customer } = useContext(CockpitCustomerContext);
  const isNew = termSheetId === 'new';

  const [name, setName] = useState('');
  const [payload, setPayload] = useState(() => createDefaultTermSheetPayload());
  const [sequenceNumber, setSequenceNumber] = useState(null);
  const [sentToClient, setSentToClient] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [savedModels, setSavedModels] = useState([]);
  const [isPrefilling, setIsPrefilling] = useState(false);

  const fields = payload.fields;
  const selectedModelId = payload.sources?.quickCashflowModelId || '';

  const setField = useCallback((key, value) => {
    setPayload((current) => ({
      ...current,
      fields: {
        ...current.fields,
        [key]: value,
      },
      fieldSources: {
        ...current.fieldSources,
        [key]: { source: 'manual' },
      },
    }));
  }, []);

  const loadTermSheet = useCallback(async () => {
    if (isNew) {
      const sourceModelId = searchParams.get('sourceModelId');
      let next = createDefaultTermSheetPayload({ sourceModelId });
      if (sourceModelId) {
        try {
          const doc = await getQuickCashflowModel(sourceModelId);
          next = applyQuickCashflowPrefillToPayload(next, {
            modelId: doc.id || sourceModelId,
            modelName: doc.name,
            modelJson: doc.model,
          });
        } catch (error) {
          toast({
            title: 'Failed to load First Underwriting model',
            description: error?.message || String(error),
            status: 'error',
          });
        }
      }
      setPayload(next);
      setName('');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const doc = await getTermSheetApi({ customerId, termSheetId });
      setName(doc.name || '');
      setPayload(parseTermSheetPayload(doc.payloadJson));
      setSequenceNumber(doc.sequenceNumber);
      setSentToClient(Boolean(doc.sentToClient));
    } catch (error) {
      toast({
        title: 'Failed to load term sheet',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customerId, isNew, searchParams, termSheetId, toast]);

  useEffect(() => {
    loadTermSheet();
  }, [loadTermSheet]);

  useEffect(() => {
    if (!customerId) {
      return;
    }
    let cancelled = false;
    listQuickCashflowModels(customerId)
      .then((rows) => {
        if (!cancelled) {
          setSavedModels(rows || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSavedModels([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const modelOptions = useMemo(() => {
    const rows = [...savedModels];
    if (selectedModelId && !rows.some((model) => model.id === selectedModelId)) {
      rows.unshift({
        id: selectedModelId,
        name: payload.sources?.quickCashflowModelName || 'Selected model',
      });
    }
    return rows;
  }, [payload.sources?.quickCashflowModelName, savedModels, selectedModelId]);

  const applyUnderwritingModel = async (modelId) => {
    if (!modelId) {
      setPayload((current) => ({
        ...current,
        sources: {
          ...current.sources,
          quickCashflowModelId: null,
          quickCashflowModelName: null,
          prefilledAt: null,
        },
      }));
      return;
    }
    setIsPrefilling(true);
    try {
      const doc = await getQuickCashflowModel(modelId);
      setPayload((current) =>
        applyQuickCashflowPrefillToPayload(current, {
          modelId: doc.id || modelId,
          modelName: doc.name,
          modelJson: doc.model,
        })
      );
    } catch (error) {
      toast({
        title: 'Failed to load First Underwriting model',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsPrefilling(false);
    }
  };

  useEffect(() => {
    if (!isNew || !customer?.name) {
      return;
    }
    setPayload((current) => {
      if (current.fields.companyName) {
        return current;
      }
      return {
        ...current,
        fields: {
          ...current.fields,
          companyName: customer.name,
        },
        fieldSources: {
          ...current.fieldSources,
          companyName: { source: 'customer' },
        },
      };
    });
  }, [isNew, customer?.name]);

  const previewParagraphs = useMemo(
    () => buildTermSheetPreviewParagraphs(fields),
    [fields]
  );

  const totalCohortInvestment = computeTotalCohortInvestment(fields);

  const updateRoasThreshold = (id, patch) => {
    setField(
      'roasThresholds',
      fields.roasThresholds.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const addRoasThreshold = () => {
    const nextId = Math.max(...fields.roasThresholds.map((row) => row.id), 0) + 1;
    setField('roasThresholds', [...fields.roasThresholds, createEmptyRoasThreshold(nextId)]);
  };

  const removeRoasThreshold = (id) => {
    const next = fields.roasThresholds.filter((row) => row.id !== id);
    setField('roasThresholds', next.length ? next : [createEmptyRoasThreshold()]);
  };

  const setFundingFeeType = (feeType) => {
    setPayload((current) => ({
      ...current,
      fields: {
        ...current.fields,
        fundingFeeType: feeType,
        fundingFeeUsd:
          feeType === FUNDING_FEE_TYPE_PCT ? '' : current.fields.fundingFeeUsd,
        fundingFeePct:
          feeType === FUNDING_FEE_TYPE_USD ? '' : current.fields.fundingFeePct,
      },
      fieldSources: {
        ...current.fieldSources,
        fundingFeeType: { source: 'manual' },
        ...(feeType === FUNDING_FEE_TYPE_PCT
          ? { fundingFeeUsd: { source: 'manual' } }
          : {}),
        ...(feeType === FUNDING_FEE_TYPE_USD
          ? { fundingFeePct: { source: 'manual' } }
          : {}),
      },
    }));
  };

  const onSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', status: 'warning' });
      return;
    }
    setIsSaving(true);
    try {
      const response = await saveTermSheetApi({
        customerId,
        data: {
          id: isNew ? undefined : termSheetId,
          name: name.trim(),
          payloadJson: serializeTermSheetPayload(payload),
        },
      });
      toast({ title: 'Term sheet saved', status: 'success' });
      if (isNew) {
        navigate(`/customers/${customerId}/term-sheets/${response.id}`, { replace: true });
      } else {
        setSequenceNumber(response.sequenceNumber ?? sequenceNumber);
      }
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onDownloadWord = async () => {
    setIsDownloadingWord(true);
    try {
      await downloadTermSheetDocx({
        fields,
        filenameBase: [customer?.name, name, sequenceNumber].filter(Boolean).join('-') || 'term-sheet',
      });
    } catch (error) {
      toast({
        title: 'Word download failed',
        description: error?.message || String(error),
        status: 'error',
      });
    } finally {
      setIsDownloadingWord(false);
    }
  };

  const onExportToDrive = async () => {
    setIsExporting(true);
    try {
      const result = await exportTermSheetToDrive({
        customerId,
        fields,
        name,
        customerName: customer?.name,
        sequenceNumber,
      });
      toast(termSheetDriveExportSuccessToast(result));
    } catch (error) {
      toast({
        title: 'Save to Drive failed',
        description:
          error?.response?.data?.error ||
          error?.message ||
          'Could not upload the export to Google Drive.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Text>Loading…</Text>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">
            Term Sheet{sequenceNumber != null ? ` #${sequenceNumber}` : ''}
          </Heading>
          <Text color="gray.600" mt={1}>
            {customer?.name || customerId}
            {sentToClient ? ' · Sent to client' : ''}
          </Text>
        </Box>
        <HStack>
          <Button variant="outline" onClick={() => navigate(`/customers/${customerId}/term-sheets`)}>
            Back to list
          </Button>
          <Button variant="outline" onClick={onDownloadWord} isLoading={isDownloadingWord}>
            Download Word
          </Button>
          <Button variant="outline" onClick={onExportToDrive} isLoading={isExporting}>
            Export to Google Drive
          </Button>
          <Button colorScheme="brand" onClick={onSave} isLoading={isSaving}>
            Save
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
        <Stack spacing={6}>
          <Card p={6}>
            <FormControl mb={4}>
              <FormLabel>Term sheet name</FormLabel>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </FormControl>
            <FormControl mb={6}>
              <FormLabel>First underwriting model</FormLabel>
              <Select
                placeholder={
                  modelOptions.length
                    ? 'Select a model to prefill…'
                    : 'No saved First Underwriting models'
                }
                value={selectedModelId}
                onChange={(event) => applyUnderwritingModel(event.target.value)}
                isDisabled={!modelOptions.length || isPrefilling}
              >
                {modelOptions.map((model) => (
                  <option key={model.id} value={model.id}>
                    {formatQuickCashflowModelOptionLabel(model)}
                  </option>
                ))}
              </Select>
            </FormControl>

            <Heading size="sm" mb={3}>
              Company
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <FormControl>
                <FormLabel>Company name</FormLabel>
                <Input
                  value={fields.companyName}
                  onChange={(event) => setField('companyName', event.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Jurisdiction</FormLabel>
                <Input
                  value={fields.companyJurisdiction}
                  onChange={(event) => setField('companyJurisdiction', event.target.value)}
                  placeholder="Delaware"
                />
              </FormControl>
              <FormControl>
                <FormLabel>File number</FormLabel>
                <Input
                  value={fields.companyFileNumber}
                  onChange={(event) => setField('companyFileNumber', event.target.value)}
                  placeholder="1234567"
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Investment Size
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <FormControl>
                <FormLabel>Max outstanding investment</FormLabel>
                <TwNumberInput
                  value={fields.maxOutstandingInvestment}
                  onChange={(value) => setField('maxOutstandingInvestment', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Expanded amount</FormLabel>
                <TwNumberInput
                  value={fields.expandedAmount}
                  onChange={(value) => setField('expandedAmount', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel># of monthly cohorts</FormLabel>
                <TwNumberInput
                  value={fields.monthlyCohorts}
                  min={0}
                  onChange={(value) => setField('monthlyCohorts', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max amount per cohort</FormLabel>
                <TwNumberInput
                  value={fields.maxAmountPerCohort}
                  onChange={(value) => setField('maxAmountPerCohort', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Total cohort investment (computed)</FormLabel>
                <Input
                  isReadOnly
                  value={
                    totalCohortInvestment != null
                      ? totalCohortInvestment.toLocaleString('en-US')
                      : ''
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>% of funding</FormLabel>
                <PercentageNumberInput
                  value={fields.pctOfFunding}
                  onChange={(value) => setField('pctOfFunding', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max % increase from last investment</FormLabel>
                <PercentageNumberInput
                  value={fields.maxPctIncreaseFromLast}
                  onChange={(value) => setField('maxPctIncreaseFromLast', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max amount increase from last investment</FormLabel>
                <TwNumberInput
                  value={fields.maxAmountIncreaseFromLast}
                  onChange={(value) => setField('maxAmountIncreaseFromLast', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Min investment</FormLabel>
                <TwNumberInput
                  value={fields.minInvestment}
                  onChange={(value) => setField('minInvestment', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max CAC</FormLabel>
                <TwNumberInput
                  value={fields.maxCac}
                  onChange={(value) => setField('maxCac', value)}
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Term
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <FormControl>
                <FormLabel># of months</FormLabel>
                <TwNumberInput
                  value={fields.termMonths}
                  min={0}
                  onChange={(value) => setField('termMonths', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel># of cohorts</FormLabel>
                <TwNumberInput
                  value={fields.termCohorts}
                  min={0}
                  onChange={(value) => setField('termCohorts', value)}
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Non-Utilization Fee
            </Heading>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel htmlFor="non-utilization-enabled" mb={0} mr={3}>
                Include Non-Utilization Fee section
              </FormLabel>
              <Switch
                id="non-utilization-enabled"
                isChecked={Boolean(fields.nonUtilizationFeeEnabled)}
                onChange={(event) =>
                  setField('nonUtilizationFeeEnabled', event.target.checked)
                }
              />
            </FormControl>
            {fields.nonUtilizationFeeEnabled ? (
              <FormControl mb={6}>
                <FormLabel>Wording</FormLabel>
                <Textarea
                  value={fields.nonUtilizationFeeWording}
                  onChange={(event) =>
                    setField('nonUtilizationFeeWording', event.target.value)
                  }
                  rows={3}
                  placeholder="Enter the Non-Utilization Fee clause wording"
                />
              </FormControl>
            ) : null}

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Warrants
            </Heading>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel htmlFor="warrants-enabled" mb={0} mr={3}>
                Include Warrants section
              </FormLabel>
              <Switch
                id="warrants-enabled"
                isChecked={Boolean(fields.warrantsEnabled)}
                onChange={(event) => setField('warrantsEnabled', event.target.checked)}
              />
            </FormControl>
            {fields.warrantsEnabled ? (
              <FormControl mb={6}>
                <FormLabel>Wording</FormLabel>
                <Textarea
                  value={fields.warrantsWording}
                  onChange={(event) => setField('warrantsWording', event.target.value)}
                  rows={3}
                  placeholder="Enter the Warrants clause wording"
                />
              </FormControl>
            ) : null}

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Repayment mechanism
            </Heading>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel htmlFor="roas-minimum-enabled" mb={0} mr={3}>
                Include minimum ROAS curve table
              </FormLabel>
              <Switch
                id="roas-minimum-enabled"
                isChecked={Boolean(fields.roasMinimumEnabled)}
                onChange={(event) => setField('roasMinimumEnabled', event.target.checked)}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Minimum ROAS curve (month number and %)
            </Text>
            <Stack spacing={3} mb={4} opacity={fields.roasMinimumEnabled ? 1 : 0.5}>
              {fields.roasThresholds.map((row) => (
                <HStack key={row.id} align="flex-end">
                  <FormControl isDisabled={!fields.roasMinimumEnabled}>
                    <FormLabel>Month</FormLabel>
                    <TwNumberInput
                      value={row.month}
                      min={0}
                      isDisabled={!fields.roasMinimumEnabled}
                      onChange={(value) => updateRoasThreshold(row.id, { month: value })}
                    />
                  </FormControl>
                  <FormControl isDisabled={!fields.roasMinimumEnabled}>
                    <FormLabel>Min ROAS %</FormLabel>
                    <PercentageNumberInput
                      value={row.minRoasPct}
                      isDisabled={!fields.roasMinimumEnabled}
                      onChange={(value) => updateRoasThreshold(row.id, { minRoasPct: value })}
                    />
                  </FormControl>
                  <IconButton
                    aria-label="Remove ROAS row"
                    icon={<DeleteIcon />}
                    onClick={() => removeRoasThreshold(row.id)}
                    isDisabled={!fields.roasMinimumEnabled || fields.roasThresholds.length === 1}
                  />
                </HStack>
              ))}
              <Button
                leftIcon={<AddIcon />}
                size="sm"
                variant="outline"
                onClick={addRoasThreshold}
                isDisabled={!fields.roasMinimumEnabled}
              >
                Add ROAS row
              </Button>
            </Stack>
            <FormControl mb={4} isDisabled={!fields.roasMinimumEnabled}>
              <FormLabel>MoM delta %</FormLabel>
              <PercentageNumberInput
                value={fields.momDeltaPct}
                isDisabled={!fields.roasMinimumEnabled}
                onChange={(value) => setField('momDeltaPct', value)}
              />
            </FormControl>
            <FormControl mb={6}>
              <FormLabel>Cohort cash flow footnote</FormLabel>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Shown at the bottom of the repayment section and referenced with ** after cohort
                cash flow in the first bullet.
              </Text>
              <Textarea
                value={fields.cohortCashFlowFootnote}
                onChange={(event) => setField('cohortCashFlowFootnote', event.target.value)}
                rows={3}
                placeholder={DEFAULT_COHORT_CASH_FLOW_FOOTNOTE}
              />
            </FormControl>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Security
            </Heading>
            <FormControl mb={6}>
              <FormLabel>Wording</FormLabel>
              <Textarea
                value={fields.securityWording}
                onChange={(event) => setField('securityWording', event.target.value)}
                rows={3}
                placeholder={DEFAULT_SECURITY_WORDING}
              />
            </FormControl>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Capped Return
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <FormControl>
                <FormLabel>IRR</FormLabel>
                <PercentageNumberInput
                  value={fields.cappedReturnIrr}
                  onChange={(value) => setField('cappedReturnIrr', value)}
                  showPrefix
                />
              </FormControl>
              <FormControl>
                <FormLabel>Total fee per cohort</FormLabel>
                <PercentageNumberInput
                  value={fields.cappedReturnTotalFeePerCohort}
                  onChange={(value) => setField('cappedReturnTotalFeePerCohort', value)}
                  showPrefix
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Funding fee
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Fee type</FormLabel>
              <RadioGroup
                value={fields.fundingFeeType || ''}
                onChange={setFundingFeeType}
              >
                <HStack spacing={6}>
                  <Radio value={FUNDING_FEE_TYPE_USD}>Fixed amount ($)</Radio>
                  <Radio value={FUNDING_FEE_TYPE_PCT}>Percentage (%)</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              {fields.fundingFeeType === FUNDING_FEE_TYPE_USD ? (
                <FormControl>
                  <FormLabel>One time fee $</FormLabel>
                  <TwNumberInput
                    value={fields.fundingFeeUsd}
                    onChange={(value) => setField('fundingFeeUsd', value)}
                  />
                </FormControl>
              ) : null}
              {fields.fundingFeeType === FUNDING_FEE_TYPE_PCT ? (
                <FormControl>
                  <FormLabel>One time fee % of investment size</FormLabel>
                  <PercentageNumberInput
                    value={fields.fundingFeePct}
                    onChange={(value) => setField('fundingFeePct', value)}
                  />
                </FormControl>
              ) : null}
              <FormControl>
                <FormLabel>Number of installments</FormLabel>
                <TwNumberInput
                  value={fields.fundingFeeInstallments}
                  min={1}
                  onChange={(value) => setField('fundingFeeInstallments', value)}
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Minimum liquidity
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel># of months of opex</FormLabel>
                <TwNumberInput
                  value={fields.minLiquidityMonths}
                  min={0}
                  onChange={(value) => setField('minLiquidityMonths', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Minimum $ amount</FormLabel>
                <TwNumberInput
                  value={fields.minLiquidityUsd}
                  onChange={(value) => setField('minLiquidityUsd', value)}
                />
              </FormControl>
            </SimpleGrid>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Confidentiality
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Have NDA</FormLabel>
              <RadioGroup
                value={fields.confidentialityHasNda ? 'yes' : 'no'}
                onChange={(value) => setField('confidentialityHasNda', value === 'yes')}
              >
                <HStack spacing={6}>
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
            {fields.confidentialityHasNda ? (
              <FormControl mb={6}>
                <FormLabel>NDA date</FormLabel>
                <TwDatePicker
                  value={fields.confidentialityNdaDate}
                  dateFormat="MM/dd/yyyy"
                  onChange={(value) => setField('confidentialityNdaDate', value ?? '')}
                />
              </FormControl>
            ) : (
              <Box mb={6} />
            )}

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              No Shop
            </Heading>
            <FormControl mb={6}>
              <FormLabel>Number of days</FormLabel>
              <TwNumberInput
                value={fields.noShopDays}
                min={0}
                onChange={(value) => setField('noShopDays', value)}
              />
            </FormControl>

            <Divider my={6} borderColor="gray.300" borderWidth="2px" />

            <Heading size="sm" mb={3}>
              Costs and Expenses
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <FormControl>
                <FormLabel>Max charges</FormLabel>
                <TwNumberInput
                  value={fields.costsAndExpensesMaxCharges}
                  onChange={(value) => setField('costsAndExpensesMaxCharges', value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Upfront fee</FormLabel>
                <TwNumberInput
                  value={fields.costsAndExpensesUpfrontFee}
                  onChange={(value) => setField('costsAndExpensesUpfrontFee', value)}
                />
              </FormControl>
            </SimpleGrid>
          </Card>
        </Stack>

        <Card p={6}>
          <Heading size="sm" mb={4}>
            Preview
          </Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Yellow highlights show the values you control in the editor. Download Word or export the
            Word document to Legal &amp; Admin → LOI in Drive.
          </Text>
          <Divider mb={4} />
          {previewParagraphs.map((paragraph) => (
            <PreviewParagraph
              key={paragraph.title}
              title={paragraph.title}
              text={paragraph.text}
            />
          ))}
        </Card>
      </SimpleGrid>
    </Box>
  );
}
